import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useMemo } from "react";
import { Booking } from "@/hooks/useBookings";

interface BlockedDate {
  property_id: string;
  blocked_date: string;
}

interface CalendarDataResponse {
  bookings: Booking[];
  blocked_dates: BlockedDate[];
}

export interface DayStatus {
  isPast: boolean;
  isBlocked: boolean;
  isBooked: boolean;
  canClick: boolean;
}

export const useCalendarData = (
  propertyIds: string[],
  startDate: Date,
  endDate: Date
) => {
  const queryKey = [
    "calendar-data",
    propertyIds.sort().join(","),
    format(startDate, "yyyy-MM-dd"),
    format(endDate, "yyyy-MM-dd"),
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_calendar_data", {
        p_property_ids: propertyIds,
        p_start_date: format(startDate, "yyyy-MM-dd"),
        p_end_date: format(endDate, "yyyy-MM-dd"),
      });

      if (error) throw error;
      
      // Parse JSON response from RPC
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
      return parsedData as CalendarDataResponse;
    },
    enabled: propertyIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Pre-process data for O(1) lookups
  const processedData = useMemo(() => {
    if (!data) {
      return {
        dayStatusByPropertyAndDate: new Map<string, Map<string, DayStatus>>(),
        bookingsByProperty: {} as Record<string, Booking[]>,
        blockedDatesByProperty: new Map<string, Set<string>>(),
      };
    }

    const dayStatusMap = new Map<string, Map<string, DayStatus>>();
    const bookingsRecord: Record<string, Booking[]> = {};
    const blockedDatesMap = new Map<string, Set<string>>();

    // Process blocked dates
    data.blocked_dates?.forEach((blocked) => {
      if (!blockedDatesMap.has(blocked.property_id)) {
        blockedDatesMap.set(blocked.property_id, new Set());
      }
      blockedDatesMap.get(blocked.property_id)!.add(blocked.blocked_date);
    });

    // Process bookings
    data.bookings?.forEach((booking) => {
      if (!bookingsRecord[booking.property_id]) {
        bookingsRecord[booking.property_id] = [];
      }
      bookingsRecord[booking.property_id].push(booking);
    });

    // Pre-calculate day status for each property
    propertyIds.forEach((propertyId) => {
      const propertyStatusMap = new Map<string, DayStatus>();
      const blockedDates = blockedDatesMap.get(propertyId) || new Set();
      const bookings = bookingsRecord[propertyId] || [];

      // Calculate status for each day in range
      const currentDate = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      while (currentDate <= end) {
        const dateStr = format(currentDate, "yyyy-MM-dd");
        const isPast = currentDate < today;
        const isManuallyBlocked = blockedDates.has(dateStr);

        // Check if day is booked
        const isBooked = bookings.some((booking) => {
          return dateStr >= booking.check_in && dateStr < booking.check_out;
        });

        const isBlocked = isManuallyBlocked || isBooked;

        propertyStatusMap.set(dateStr, {
          isPast,
          isBlocked,
          isBooked,
          canClick: !isPast,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      dayStatusMap.set(propertyId, propertyStatusMap);
    });

    return {
      dayStatusByPropertyAndDate: dayStatusMap,
      bookingsByProperty: bookingsRecord,
      blockedDatesByProperty: blockedDatesMap,
    };
  }, [data, propertyIds, startDate, endDate]);

  // O(1) lookup functions
  const getDayStatus = (date: Date, propertyId: string): DayStatus => {
    const dateStr = format(date, "yyyy-MM-dd");
    const propertyMap = processedData.dayStatusByPropertyAndDate.get(propertyId);
    
    if (!propertyMap) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPast = date < today;
      
      return {
        isPast,
        isBlocked: false,
        isBooked: false,
        canClick: !isPast,
      };
    }

    return (
      propertyMap.get(dateStr) || {
        isPast: false,
        isBlocked: false,
        isBooked: false,
        canClick: true,
      }
    );
  };

  const getBookingForDay = (
    date: Date,
    propertyId: string
  ): Booking | undefined => {
    const dateStr = format(date, "yyyy-MM-dd");
    const bookings = processedData.bookingsByProperty[propertyId] || [];

    return bookings.find(
      (booking) => dateStr >= booking.check_in && dateStr < booking.check_out
    );
  };

  return {
    getDayStatus,
    getBookingForDay,
    bookingsByProperty: processedData.bookingsByProperty,
    blockedDatesByProperty: processedData.blockedDatesByProperty,
    isLoading,
    error,
    refetch,
  };
};
